import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { QrCode, Search, RotateCcw, Ban, ExternalLink, Users } from "lucide-react";

export default function PatientQR() {
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadPatients = async () => {
    try {
      const { data } = await api.get("/patients");
      setPatients(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  useEffect(() => {
    loadPatients();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return patients.slice(0, 50);
    return patients
      .filter((p) =>
        [p.full_name, p.patient_number, p.contact_number]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle)
      )
      .slice(0, 50);
  }, [patients, q]);

  const selectPatient = async (p) => {
    setSelected(p);
    setTokenInfo(null);
    setBusy(true);
    try {
      const { data } = await api.get(`/patients/${p.id}/qr-token`);
      setTokenInfo(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/patients/${selected.id}/qr-token`);
      setTokenInfo(data);
      toast.success(data.qr_url ? "Patient QR generated" : "Patient already has active QR token");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!selected) return;
    if (!window.confirm(`Revoke QR token for ${selected.full_name}?`)) return;

    setBusy(true);
    try {
      await api.delete(`/patients/${selected.id}/qr-token`);
      toast.success("Patient QR revoked");
      const { data } = await api.get(`/patients/${selected.id}/qr-token`);
      setTokenInfo(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  const fullUrl = tokenInfo?.qr_url
    ? `${window.location.origin}${tokenInfo.qr_url.startsWith("/") ? tokenInfo.qr_url : `/${tokenInfo.qr_url}`}`
    : "";

  return (
    <div className="p-6 md:p-8 max-w-[1400px]" data-testid="patient-qr-page">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>
          Patient QR Cards
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Generate secure patient QR shortcuts. Full medical records still require authorized staff login.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-5">
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users size={16} />
              Patients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative mb-3">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient..." />
            </div>

            <div className="rounded-md border border-slate-200 divide-y max-h-[68vh] overflow-auto">
              {filtered.map((p) => (
                <button
                  key={p.id}
                  onClick={() => selectPatient(p)}
                  className={`w-full text-left p-3 hover:bg-slate-50 ${selected?.id === p.id ? "bg-blue-50" : ""}`}
                >
                  <div className="font-medium text-slate-900">{p.full_name}</div>
                  <div className="text-xs text-slate-500 mt-1">{p.patient_number || p.contact_number || "—"}</div>
                </button>
              ))}
              {filtered.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400">No patients found.</div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <QrCode size={16} />
              QR Details
            </CardTitle>
          </CardHeader>

          <CardContent>
            {!selected ? (
              <div className="p-12 text-center text-slate-400">Select a patient to manage QR card.</div>
            ) : (
              <div className="space-y-5">
                <div className="rounded-xl border border-slate-200 p-5 bg-slate-50">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Selected Patient</div>
                  <div className="text-2xl font-semibold text-slate-900 mt-1">{selected.full_name}</div>
                  <div className="text-sm text-slate-500 mt-1">
                    {[selected.patient_number, selected.age ? `${selected.age} yrs` : "", selected.gender, selected.contact_number]
                      .filter(Boolean)
                      .join(" · ") || "No extra details"}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-slate-900">QR Token Status</div>
                      <div className="text-sm text-slate-500 mt-1">
                        {tokenInfo?.note || "Generate a new secure QR token for this patient."}
                      </div>
                    </div>
                    <Badge variant={tokenInfo?.has_active_token || tokenInfo?.active ? "default" : "secondary"} className={tokenInfo?.has_active_token || tokenInfo?.active ? "bg-emerald-600" : ""}>
                      {tokenInfo?.has_active_token || tokenInfo?.active ? "Active" : "No Active Token"}
                    </Badge>
                  </div>

                  {fullUrl ? (
                    <div className="mt-5 rounded-lg bg-blue-50 border border-blue-100 p-4">
                      <div className="text-xs uppercase tracking-wider text-blue-700 font-medium">New QR URL</div>
                      <div className="font-mono text-xs text-blue-900 break-all mt-2">{fullUrl}</div>
                      <Button asChild variant="outline" size="sm" className="mt-3">
                        <a href={fullUrl} target="_blank" rel="noreferrer">
                          <ExternalLink size={14} className="mr-1" />
                          Open Patient Card
                        </a>
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-5 text-sm text-slate-500">
                      Existing QR tokens are not shown again for security. Revoke and generate a new one if a QR needs to be reprinted.
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 mt-5">
                    <Button onClick={generate} disabled={busy} className="bg-blue-600 hover:bg-blue-700">
                      <QrCode size={15} className="mr-2" />
                      Generate / Get QR
                    </Button>
                    <Button onClick={revoke} disabled={busy} variant="outline" className="text-red-700">
                      <Ban size={15} className="mr-2" />
                      Revoke QR
                    </Button>
                    <Button onClick={() => selected && selectPatient(selected)} disabled={busy} variant="outline">
                      <RotateCcw size={15} className="mr-2" />
                      Refresh
                    </Button>
                  </div>
                </div>

                <div className="text-xs text-slate-500">
                  Security note: the public QR card only exposes limited patient identity data. Full records stay behind login.
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
